/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from "react";
import { ITextFieldStyles, mergeStyleSets, TextField } from "@fluentui/react";
import * as Messages from "../../codegen/Messages";
import { FilterAll } from "../../constants/uiConstants";
import { ListingColumn } from "../Listing/ListingTypes";
import { FilterOperationModal, IDropdownOptionType, IDropdownOptionWithType } from "./FilterOperationModal";
import { ExtraFilter, FilterComponent, FilterLayout, Operator, searchFieldTestId } from "./FilterTypes";
import { filterDataByExtraFilter, filterDataByTextFilter } from "./FilterUtils";

export interface FiltersProps<T> {
  /**
   * Items data input list
   */
  items: T[];

  /**
   * Columns of data input list
   */
  columns: ListingColumn[];

  /**
   * CallBack function which is used to return the filtered data items
   */
  setFilteredItems: React.Dispatch<React.SetStateAction<T[]>>;

  /**
   * All the available selected extra filters
   */
  allExtraFilters?: ExtraFilter[];

  /**
   * Initial default selected extra filters
   */
  defaultExtraFilters?: ExtraFilter[];

  /**
   * Hide text input select filter
   */
  hideTextFilter?: boolean;

  /**
   * Hide add filter button
   */
  hideAddFilterPill?: boolean;

  /**
   * Layout type for the Filter
   */
  filterLayout?: FilterLayout;

  /**
   * Text filter callback function
   */
  textFilterCallBack?: (item: any) => any;

  /**
   * Component reference for the FilterComponent
   */
  componentRef?: (ref: FilterComponent) => void;

  /**
   * Default filter text value
   */
  defaultFilterText?: string;

}

const classNames = mergeStyleSets({
  filterContainer: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
  },
  filterInputContainer: { paddingRight: "10px" },
  dropdownLayoutFilterInputContainer: {
    paddingRight: "10px",
    paddingTop: "2px",
  },
  filterItemsContainer: { display: "flex", flexWrap: "wrap" },
  dropdownLayoutFilterItemsContainer: {
    display: "flex",
    flexWrap: "wrap",
  },
  filterItems: {
    borderColor: "#e6f2fb",
    backgroundColor: "#e6f2fb",
    margin: "5px",
    fontSize: "13px",
    borderRadius: "16px",
    boxSizing: "border-box",
    border: "1px solid",
    cursor: "pointer",
  },
});

const textFieldStyles: Partial<ITextFieldStyles> = {
  fieldGroup: {
    width: 200,
    height: 24,
    color: "#323130",
    borderColor: "#8a8886",
  },
};

/**
 *
 * Filters is a component to show filter section above the list.
 * It provides a textfield search and select box searches based on a selected field
 */
// eslint-disable-next-line react/function-component-definition
export function Filters<T>({
  items,
  columns,
  setFilteredItems,
  defaultExtraFilters,
  allExtraFilters,
  hideTextFilter = false,
  hideAddFilterPill = false,
  filterLayout = FilterLayout.Pill,
  textFilterCallBack,
  componentRef,
  defaultFilterText,
}: FiltersProps<T>): JSX.Element {
  const [filterText, setFilterText] = React.useState<string | undefined>(defaultFilterText || "");
  const [extraFilters, setExtraFilters] = React.useState<ExtraFilter[]>(defaultExtraFilters || []);
  const [itemsInList, setItemsInList] = React.useState(items);
  const thisRef = React.useRef<FilterComponent>();

  React.useEffect(() => {
    setItemsInList(items);
  }, [items]);

  React.useEffect(() => {
    if (defaultExtraFilters) {
      const tempArr = [...extraFilters];
      tempArr.forEach(t => {
        const item = defaultExtraFilters.find(f => f.fieldKey === t.fieldKey);
        if (item && item.customOptions) {
          t.customOptions = item?.customOptions;
          t.value = item.value;
          if (item.callBack) {
            t.callBack = item.callBack;
          }
        }
      });
      setExtraFilters(tempArr);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultExtraFilters]);

  React.useEffect(() => {
    if (allExtraFilters) {
      const tempArr = [...extraFilters];
      tempArr.forEach(t => {
        const item = allExtraFilters.find(f => f.fieldKey === t.fieldKey);
        if (item && item.customOptions) {
          t.customOptions = item?.customOptions;
        }
      });
      setExtraFilters(tempArr);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allExtraFilters]);

  const itemsTagsOptions: IDropdownOptionWithType[] = [];
  items.forEach(item => {
    if ((item as any)?.freeformTags) {
      const tagKeys = Object.keys((item as any).freeformTags);
      tagKeys.forEach(tagKey => {
        const findKeyExist = itemsTagsOptions?.find(f => f.key.toString().toLowerCase() === tagKey.toLowerCase());
        if (!findKeyExist) {
          itemsTagsOptions.push({
            key: tagKey.toLowerCase(),
            text: tagKey,
            type: IDropdownOptionType.Tag,
          });
        }
      });
    }
  });
  itemsTagsOptions.forEach(o => {
    const tempFilter: ExtraFilter = {
      name: o.text.toString(),
      operator: Operator.Equal,
      value: [FilterAll],
      fieldKey: o.key.toString(),
      isTagFilter: true,
    };
    const findExist = allExtraFilters?.find(f => f.fieldKey === o.key);
    if (!findExist) {
      allExtraFilters?.push(tempFilter);
    }
  });

  React.useEffect(() => {
    if (componentRef) {
      thisRef.current = {
        getFilters: () => extraFilters.map(
          tempFilter => ({ fieldKey: tempFilter.fieldKey, value: tempFilter.value }),
        ),
        getFilterText: () => filterText,
      } as FilterComponent;
      componentRef(thisRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(extraFilters), filterText]);

  React.useEffect(() => {
    let tempItemsInList = items;
    if (filterText) {
      if (textFilterCallBack) {
        tempItemsInList = items.filter(item => (filterDataByTextFilter(textFilterCallBack(item), filterText, columns)));
      } else {
        tempItemsInList = items.filter(item => (filterDataByTextFilter(item, filterText, columns)));
      }
    }
    if (extraFilters) {
      extraFilters.forEach((extraFilter: ExtraFilter) => {
        if (!extraFilter?.callBack) {
          tempItemsInList = filterDataByExtraFilter(tempItemsInList, extraFilter);
        }
      });
    }
    setItemsInList(tempItemsInList);
    setFilteredItems(tempItemsInList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterText, extraFilters]);

  const onTextFilter = (
    _event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    text?: string | undefined,
  ): void => {
    setFilterText(text);
  };

  return (
    <div className={classNames.filterContainer}>
      {!hideTextFilter && (
        <div
          className={
            filterLayout === FilterLayout.Pill
              ? classNames.filterInputContainer
              : classNames.dropdownLayoutFilterInputContainer
          }
        >
          <TextField
            data-test-id={searchFieldTestId}
            onChange={onTextFilter}
            styles={textFieldStyles}
            placeholder={Messages.hints.filterAnyField()}
            value={filterText}
          />
        </div>
      )}
      <div
        className={
          filterLayout === FilterLayout.Pill
            ? classNames.filterItemsContainer
            : classNames.dropdownLayoutFilterItemsContainer
        }
      >
        {extraFilters.map((extraFilterItem, itemIndex) => (
          <FilterOperationModal
            // eslint-disable-next-line react/no-array-index-key
            key={`extra-filter-${itemIndex}`}
            title={extraFilterItem.name}
            itemsInList={itemsInList}
            items={items}
            defaultExtraFilter={extraFilterItem}
            allExtraFilters={allExtraFilters}
            columns={columns}
            textFilterCallBack={textFilterCallBack}
            currentExtraFilters={extraFilters}
            currentFilterText={filterText}
            extraFiltersIgnored={extraFilters}
            filterLayout={filterLayout}
            onApply={(extraFilter: ExtraFilter): void => {
              if (extraFilter) {
                const newExtraFilters = [...extraFilters];
                newExtraFilters[itemIndex] = extraFilter;
                setExtraFilters(newExtraFilters);
              }
            }}
            onDelete={(extraFilter: ExtraFilter) => {
              setExtraFilters(extraFilters.filter(item => !(
                item.name === extraFilter.name
                && item.operator === extraFilter.operator
                && item.value === extraFilter.value
                && item.fieldKey === extraFilter.fieldKey
              )));
            }}
          />
        ))}
      </div>
      {!hideAddFilterPill && (
        <FilterOperationModal
          key="add_extra-filter"
          itemsInList={itemsInList}
          items={items}
          columns={columns}
          textFilterCallBack={textFilterCallBack}
          currentExtraFilters={extraFilters}
          currentFilterText={filterText}
          allExtraFilters={allExtraFilters}
          extraFiltersIgnored={extraFilters}
          filterLayout={filterLayout}
          onApply={(extraFilter: ExtraFilter) => {
            setExtraFilters([...extraFilters, ...[extraFilter]]);
          }}
        />
      )}
    </div>
  );
}
